import axios, {AxiosError} from 'axios'
import {API_URL} from '../../../../_metronic/helpers'
import {
  TicketApiError,
  TicketAttachment,
  TicketDetail,
  TicketListResult,
  TicketInvoice,
  TicketLogReference,
  TicketPriority,
  UploadedTicketFile,
  TicketListItem,
  TicketCatalog,
  TicketSatisfaction,
  TicketNotificationPreferences,
  TicketSatisfactionReport,
  TicketSatisfactionReportFilter,
  TicketClassificationValue,
  TicketVoluntaryCloseReason,
  TicketOpenInvoiceTicket,
} from './support-ticket-types'
import {
  canonicalTicketStatus,
  isReplyableStatus,
  statusRequiresUserAction,
  ticketStatusLabel,
} from './support-ticket-status'
import {CURRENT_APP_VERSION} from '../../../appVersion'

const base = `${API_URL}/customer-tickets`
const freshRequest = () => ({
  headers: {'Cache-Control': 'no-cache', Pragma: 'no-cache'},
  params: {_ts: Date.now()},
})

type TicketListWire = Omit<TicketListItem, 'status' | 'canonicalStatus' | 'requiresUserAction'> & {
  status: string
  canonicalStatus?: string
  requiresUserAction?: boolean
}

type TicketDetailWire = Omit<
  TicketDetail,
  | 'status'
  | 'canonicalStatus'
  | 'requiresUserAction'
  | 'rawStatus'
  | 'canReply'
  | 'requiresCloseConfirmation'
  | 'isClosed'
  | 'canConfirmClose'
  | 'canRejectClose'
  | 'canEdit'
  | 'canClose'
  | 'hasSupportResponse'
  | 'editVersion'
  | 'relatedInvoices'
> &
  TicketListWire & {
    rawStatus?: string
    canReply?: boolean
    requiresCloseConfirmation?: boolean
    isClosed?: boolean
    canConfirmClose?: boolean
    canRejectClose?: boolean
    canEdit?: boolean
    canClose?: boolean
    hasSupportResponse?: boolean
    editVersion?: string
    relatedInvoices?: TicketInvoice[]
  }

const normalizeListItem = (wire: TicketListWire): TicketListItem => {
  const canonicalStatus = canonicalTicketStatus(wire.canonicalStatus || wire.status)
  const priority = (
    [1, 2, 3, 4].includes(Number(wire.priority)) ? Number(wire.priority) : 2
  ) as TicketPriority
  const reportedUrgency = (
    [1, 2, 3, 4].includes(Number(wire.reportedUrgency)) ? Number(wire.reportedUrgency) : priority
  ) as TicketPriority
  return {
    ...wire,
    status: canonicalStatus,
    canonicalStatus,
    statusLabel: wire.statusLabel || ticketStatusLabel(canonicalStatus),
    priority,
    priorityLabel: wire.priorityLabel || 'عادی',
    reportedUrgency,
    reportedUrgencyLabel: wire.reportedUrgencyLabel || wire.priorityLabel || 'عادی',
    impactLabel: wire.impactLabel || 'بدون اثر اعلام‌شده',
    requiresUserAction:
      typeof wire.requiresUserAction === 'boolean'
        ? wire.requiresUserAction
        : statusRequiresUserAction(canonicalStatus),
  }
}

const normalizeDetail = (wire: TicketDetailWire): TicketDetail => {
  const listItem = normalizeListItem(wire)
  const pendingClose = listItem.canonicalStatus === 'PendingClose'
  const closed = listItem.canonicalStatus === 'Closed'
  const relatedInvoices = wire.relatedInvoices?.length
    ? wire.relatedInvoices
    : wire.relatedInvoice
    ? [wire.relatedInvoice]
    : []
  const hasExplicitCreationContext = (wire.messages || []).some(
    (message) => message.systemContext?.type === 'ticketCreated'
  )
  let contextAssigned = hasExplicitCreationContext
  const messages = (wire.messages || []).map((message) => {
    if (
      !contextAssigned &&
      message.senderType === 'system' &&
      !message.systemContext &&
      (relatedInvoices.length > 0 || wire.relatedPageUrl || wire.relatedLog)
    ) {
      contextAssigned = true
      return {
        ...message,
        systemContext: {
          type: 'ticketCreated',
          invoices: relatedInvoices,
          pageUrl: wire.relatedPageUrl,
          relatedLog: wire.relatedLog,
        },
      }
    }
    return message
  })
  const detail = {
    ...wire,
    ...listItem,
    rawStatus: wire.rawStatus || wire.status || '',
    canReply:
      typeof wire.canReply === 'boolean'
        ? wire.canReply
        : isReplyableStatus(listItem.canonicalStatus),
    requiresCloseConfirmation:
      typeof wire.requiresCloseConfirmation === 'boolean'
        ? wire.requiresCloseConfirmation
        : pendingClose,
    isClosed: typeof wire.isClosed === 'boolean' ? wire.isClosed : closed,
    canConfirmClose:
      typeof wire.canConfirmClose === 'boolean' ? wire.canConfirmClose : pendingClose,
    canRejectClose: typeof wire.canRejectClose === 'boolean' ? wire.canRejectClose : pendingClose,
    canEdit:
      typeof wire.canEdit === 'boolean'
        ? wire.canEdit
        : !closed && listItem.canonicalStatus !== 'Unknown',
    canClose:
      typeof wire.canClose === 'boolean'
        ? wire.canClose
        : !closed && listItem.canonicalStatus !== 'Unknown',
    hasSupportResponse:
      typeof wire.hasSupportResponse === 'boolean' ? wire.hasSupportResponse : false,
    editVersion: wire.editVersion || '',
    relatedInvoices,
    messages,
  }
  if (process.env.NODE_ENV === 'development') {
    console.debug('[Ticket status contract]', {
      rawStatus: detail.rawStatus,
      canonicalStatus: detail.canonicalStatus,
      requiresCloseConfirmation: detail.requiresCloseConfirmation,
      canReply: detail.canReply,
      isClosed: detail.isClosed,
      frontendBuildVersion: CURRENT_APP_VERSION,
    })
  }
  return detail
}

export const ticketErrorMessage = (error: unknown) => {
  const axiosError = error as AxiosError<TicketApiError>
  const message = axiosError.response?.data?.message
  const correlation = axiosError.response?.data?.correlationId
  return message
    ? `${message}${correlation ? ` (شناسه پیگیری: ${correlation})` : ''}`
    : 'در حال حاضر ارتباط با سامانه پشتیبانی برقرار نیست؛ لطفاً چند دقیقه دیگر تلاش کنید.'
}

export const getTickets = async (query: URLSearchParams) => {
  const request = new URLSearchParams(query)
  request.set('_ts', Date.now().toString())
  const response = (
    await axios.get<Omit<TicketListResult, 'items'> & {items: TicketListWire[]}>(
      `${base}?${request.toString()}`,
      {headers: freshRequest().headers}
    )
  ).data
  return {...response, items: response.items.map(normalizeListItem)}
}

export const getTicket = async (ticketNumber: string) =>
  normalizeDetail(
    (
      await axios.get<TicketDetailWire>(
        `${base}/${encodeURIComponent(ticketNumber)}`,
        freshRequest()
      )
    ).data
  )

export const createTicket = async (payload: {
  clientRequestId: string
  subject: string
  description: string
  departmentCode: string
  requestTypeCode: string
  topicCode: string
  impactCode?: string
  priority: TicketPriority
  invoiceId?: number
  invoiceIds: number[]
  appLogId?: number
  pageUrl?: string
  fileIds: string[]
  taxDiagnosticCode?: string
  taxDiagnosticChecklistConfirmed?: boolean
}) => {
  return normalizeDetail((await axios.post<TicketDetailWire>(base, payload)).data)
}

export const updateTicket = async (
  ticketNumber: string,
  payload: {
    clientRequestId: string
    expectedVersion: string
    subject: string
    urgency: TicketPriority
  } & TicketClassificationValue
) =>
  normalizeDetail(
    (await axios.patch<TicketDetailWire>(`${base}/${encodeURIComponent(ticketNumber)}`, payload))
      .data
  )

export const closeTicket = async (
  ticketNumber: string,
  payload: {
    clientRequestId: string
    expectedVersion: string
    reasonCode: TicketVoluntaryCloseReason
  }
) =>
  normalizeDetail(
    (
      await axios.post<TicketDetailWire>(
        `${base}/${encodeURIComponent(ticketNumber)}/close`,
        payload
      )
    ).data
  )

export const getTicketCatalog = async () =>
  (await axios.get<TicketCatalog>(`${base}/catalog`, freshRequest())).data

export const getTicketSatisfaction = async (ticketNumber: string) =>
  (
    await axios.get<TicketSatisfaction>(
      `${base}/${encodeURIComponent(ticketNumber)}/satisfaction`,
      freshRequest()
    )
  ).data

export const submitTicketSatisfaction = async (
  ticketNumber: string,
  payload: {clientRequestId: string; overallScore: number; effortScore: number; comment?: string},
  edit = false
) =>
  (
    await axios.request<TicketSatisfaction>({
      method: edit ? 'PUT' : 'POST',
      url: `${base}/${encodeURIComponent(ticketNumber)}/satisfaction`,
      data: payload,
    })
  ).data

export const skipTicketSatisfaction = async (ticketNumber: string) =>
  (
    await axios.post<TicketSatisfaction>(
      `${base}/${encodeURIComponent(ticketNumber)}/satisfaction/skip`
    )
  ).data

export const getTicketSatisfactionReport = async (filter: TicketSatisfactionReportFilter = {}) =>
  (
    await axios.get<TicketSatisfactionReport>(`${base}/reports/satisfaction`, {
      headers: freshRequest().headers,
      params: {...filter, _ts: Date.now()},
    })
  ).data

export const getTicketNotificationPreferences = async () =>
  (
    await axios.get<TicketNotificationPreferences>(
      `${base}/notifications/preferences`,
      freshRequest()
    )
  ).data

export const updateTicketNotificationPreferences = async (value: TicketNotificationPreferences) =>
  (
    await axios.put<TicketNotificationPreferences>(`${base}/notifications/preferences`, {
      smsEnabled: value.sms.enabled,
      telegramEnabled: value.telegram.enabled,
      events: value.events,
      quietHours: value.quietHours,
    })
  ).data

export const createTicketTelegramConnection = async () =>
  (await axios.post<TicketNotificationPreferences>(`${base}/notifications/telegram/connect`)).data

export const disconnectTicketTelegram = async () =>
  (await axios.delete<TicketNotificationPreferences>(`${base}/notifications/telegram`)).data

export const getTicketInvoiceOptions = async (search = '', take = 30, page = 1) =>
  (
    await axios.get<TicketInvoice[]>(`${base}/invoice-options`, {
      headers: freshRequest().headers,
      params: {search: search.trim() || undefined, take, page, _ts: Date.now()},
    })
  ).data

export const getOpenTicketsForInvoices = async (invoiceIds: number[]) => {
  const ids = Array.from(new Set(invoiceIds.filter((id) => Number.isSafeInteger(id) && id > 0)))
  if (ids.length === 0) return [] as TicketOpenInvoiceTicket[]

  const chunks: number[][] = []
  for (let offset = 0; offset < ids.length; offset += 50) {
    chunks.push(ids.slice(offset, offset + 50))
  }
  const responses = await Promise.all(
    chunks.map(async (chunk) => {
      const request = freshRequest()
      try {
        return (
          await axios.get<TicketOpenInvoiceTicket[]>(`${base}/open-by-invoices`, {
            ...request,
            params: {...request.params, invoiceIds: chunk.join(',')},
          })
        ).data
      } catch (error) {
        // The ticket link is optional enrichment for the invoice list. A missing CRM
        // contact or inaccessible invoice is represented by this endpoint as 404.
        if ((error as AxiosError)?.response?.status === 404) return []
        throw error
      }
    })
  )
  return responses.flat()
}

export type TicketLogFilter = 'recommended' | 'errors' | 'files' | 'all'

export const getTicketLogOptions = async (
  search = '',
  take = 30,
  filter: TicketLogFilter = 'recommended'
) =>
  (
    await axios.get<TicketLogReference[]>(`${base}/log-options`, {
      headers: freshRequest().headers,
      params: {search: search.trim() || undefined, take, filter, _ts: Date.now()},
    })
  ).data

export const replyTicket = async (
  ticketNumber: string,
  payload: {
    clientRequestId: string
    body: string
    fileIds: string[]
    invoiceIds: number[]
    /** The message this reply answers (its key; backend 1.0.0.703). */
    replyToKey?: string
  }
) => {
  const response = await axios.post<TicketDetailWire>(
    `${base}/${encodeURIComponent(ticketNumber)}/messages`,
    payload
  )
  return normalizeDetail(response.data)
}

/** «پیوستن به جلسه» (backend 1.0.0.706): the customer's coming is logged; the meeting's guest link. */
export const joinTicketMeeting = async (ticketNumber: string, room: string) =>
  (
    await axios.post<{url: string}>(
      `${base}/${encodeURIComponent(ticketNumber)}/meetings/${encodeURIComponent(room)}/join`
    )
  ).data

/**
 * «نمایش صفحه‌ی من به پشتیبانی» (backend 1.0.0.708): a live screen share of an open ticket of the customer's - the CRM's
 * door, the share's token and the ICE servers; the ticket's owner is told.
 */
export const startTicketScreenShare = async (ticketNumber: string) =>
  (
    await axios.post<{
      id: number
      token: string
      endpoint: string
      pollMs: number
      iceServers: RTCIceServer[]
    }>(`${base}/${encodeURIComponent(ticketNumber)}/screen-share`)
  ).data

/**
 * «اشتراک‌گذاری صفحه با پشتیبانی» (CRM 8.4.1.0.350): the ticket's online meeting with its own code, and the customer's link to
 * it. The backend checks the ticket is the customer's and open, as for a share; with that share's token the CRM's door gives
 * the meeting. fetch, as for every call to that door: it takes none of the panel's own headers.
 */
export const openTicketScreenMeeting = async (ticketNumber: string) => {
  const share = await startTicketScreenShare(ticketNumber)
  const response = await fetch(`${share.endpoint}?share=${share.id}&action=meeting`, {
    method: 'POST',
    headers: {'X-Asa-Share-Token': share.token},
  })
  const answer = await response.json().catch(() => null)
  if (!response.ok || !answer?.success) {
    throw Object.assign(new Error('The screen meeting was not opened.'), {
      response: {data: {message: answer?.error?.message}},
    })
  }
  return answer.result as {room: string; title: string; url: string}
}

export const confirmTicketClose = async (ticketNumber: string) =>
  normalizeDetail(
    (
      await axios.post<TicketDetailWire>(
        `${base}/${encodeURIComponent(ticketNumber)}/confirm-close`
      )
    ).data
  )

export const rejectTicketClose = async (
  ticketNumber: string,
  payload: {clientRequestId: string; message: string}
) => {
  const response = await axios.post<TicketDetailWire>(
    `${base}/${encodeURIComponent(ticketNumber)}/reject-close`,
    payload
  )
  return normalizeDetail(response.data)
}

export type TicketFileUploadScope =
  | {type: 'draft'; draftId: string}
  | {type: 'ticket'; ticketNumber: string; draftId: string}

export const uploadTicketFile = async (
  scope: TicketFileUploadScope,
  file: File,
  onProgress: (progress: number) => void
) => {
  const form = new FormData()
  form.append('file', file, file.name)
  if (scope.type === 'ticket') form.append('draftId', scope.draftId)
  const url =
    scope.type === 'draft'
      ? `${base}/drafts/${encodeURIComponent(scope.draftId)}/files`
      : `${base}/${encodeURIComponent(scope.ticketNumber)}/files`
  return (
    await axios.post<UploadedTicketFile>(url, form, {
      onUploadProgress: (event: ProgressEvent) => {
        if (event.total) onProgress(Math.min(100, Math.round((event.loaded * 100) / event.total)))
      },
    })
  ).data
}

export const deleteTicketFile = async (scope: TicketFileUploadScope, publicId: string) => {
  const url =
    scope.type === 'draft'
      ? `${base}/drafts/${encodeURIComponent(scope.draftId)}/files/${encodeURIComponent(publicId)}`
      : `${base}/${encodeURIComponent(scope.ticketNumber)}/files/${encodeURIComponent(publicId)}`
  await axios.delete(url)
}

const resolveApiUrl = (path: string) => {
  if (/^https?:\/\//i.test(path)) return path
  const configured = (API_URL || '').replace(/\/$/, '')
  if (path.startsWith('/api/')) {
    const apiIndex = configured.toLowerCase().lastIndexOf('/api')
    return `${apiIndex >= 0 ? configured.slice(0, apiIndex) : configured}${path}`
  }
  return `${configured}/${path.replace(/^\//, '')}`
}

export const getTicketAttachmentBlob = async (attachment: TicketAttachment) =>
  (await axios.get<Blob>(resolveApiUrl(attachment.downloadUrl), {responseType: 'blob'})).data

export const downloadTicketAttachment = async (attachment: TicketAttachment, preview = false) => {
  const previewWindow = preview ? window.open('', '_blank') : null
  if (previewWindow) previewWindow.opener = null
  try {
    const response = await axios.get<Blob>(resolveApiUrl(attachment.downloadUrl), {
      params: {preview: preview || undefined, download: preview ? undefined : true},
      responseType: 'blob',
    })
    const url = URL.createObjectURL(response.data)
    if (preview && previewWindow) previewWindow.location.href = url
    else {
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = attachment.originalFileName
      if (preview) anchor.target = '_blank'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
  } catch (error) {
    previewWindow?.close()
    throw error
  }
}
